/* ============================================================
   ISIS — Article uniform layout JS
   - Génère le TOC à partir des h2 du body
   - Scroll-spy : highlight section active
   - Transforme la FAQ (h3+contenu suivant) en details/summary natifs
   - Sticky mobile CTA : reveal après 40% scroll
   ============================================================ */
(function () {
  'use strict';

  function init() {
    const article = document.querySelector('.isis-article');
    if (!article) return;

    buildTOC(article);
    transformFAQ(article);
    markTableHeaderRows(article);
    setupStickyMobile(article);
  }

  /* ─── TABLE HEADER DETECTION ─── */
  // Shopify rich-text édite des tables sans <thead>/<th>. On marque la 1ère <tr>
  // comme header-row pour appliquer le style purple, sauf si la table a déjà <th>.
  function markTableHeaderRows(root) {
    const tables = root.querySelectorAll('.isis-article__body table');
    tables.forEach(t => {
      if (t.querySelector('th')) return; // table déjà bien stylée
      const firstRow = t.querySelector('tr');
      if (firstRow && !firstRow.classList.contains('is-header-row')) {
        firstRow.classList.add('is-header-row');
      }
    });
  }

  /* ─── TOC ─── */
  function slugify(text) {
    return text
      .toLowerCase()
      .normalize('NFD').replace(/[̀-ͯ]/g, '')
      .replace(/[^a-z0-9\s-]/g, '')
      .trim()
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .slice(0, 60);
  }

  function buildTOC(root) {
    const body = root.querySelector('[data-article-body]');
    const faq = root.querySelector('[data-article-faq]');
    const desktopList = root.querySelector('[data-toc-list]');
    const mobileList = root.querySelector('[data-toc-list-mobile]');
    if (!body || !desktopList) return;

    // Collecte les h2 du body + le h2 de la FAQ (s'il existe)
    const headings = Array.from(body.querySelectorAll('h2'));
    if (faq) {
      const faqH2 = faq.querySelector('h2');
      if (faqH2) headings.push(faqH2);
    }

    if (headings.length === 0) {
      // Pas de h2 → masquer le TOC
      const tocEl = root.querySelector('[data-toc]');
      if (tocEl) tocEl.style.display = 'none';
      return;
    }

    const usedIds = new Set();
    const items = headings.map((h, idx) => {
      // Préserve l'id existant ou en génère un
      let id = h.id || slugify(h.textContent || '');
      if (!id) id = 'section-' + (idx + 1);
      // Dédoublonne
      let base = id, n = 2;
      while (usedIds.has(id)) { id = base + '-' + n; n++; }
      usedIds.add(id);
      h.id = id;
      return { id, text: h.textContent.trim(), el: h };
    });

    // Build markup
    const desktopHTML = items.map(it => (
      `<li><a href="#${it.id}" data-toc-link="${it.id}">${escapeHTML(it.text)}</a></li>`
    )).join('');
    desktopList.innerHTML = desktopHTML;
    if (mobileList) {
      mobileList.innerHTML = `<ol class="isis-article__toc-list">${desktopHTML}</ol>`;
    }

    // Smooth-scroll + close mobile collapse on click
    root.querySelectorAll('[data-toc-link]').forEach(a => {
      a.addEventListener('click', (e) => {
        const id = a.getAttribute('data-toc-link');
        const target = document.getElementById(id);
        if (target) {
          e.preventDefault();
          window.scrollTo({
            top: target.getBoundingClientRect().top + window.pageYOffset - 80,
            behavior: 'smooth'
          });
          history.replaceState(null, '', '#' + id);
          // close mobile collapse
          const det = root.querySelector('.isis-article__toc-mobile-trigger details');
          if (det && det.open) det.open = false;
        }
      });
    });

    // Scroll-spy via IntersectionObserver
    if ('IntersectionObserver' in window) {
      const links = new Map();
      root.querySelectorAll('[data-toc-link]').forEach(a => {
        links.set(a.getAttribute('data-toc-link'), a);
      });

      let activeId = null;
      const setActive = (id) => {
        if (id === activeId) return;
        activeId = id;
        links.forEach(a => a.classList.remove('is-active'));
        // Mark all links matching this id (desktop + mobile)
        document.querySelectorAll(`[data-toc-link="${id}"]`).forEach(a => a.classList.add('is-active'));
      };

      const observer = new IntersectionObserver((entries) => {
        // Sort by position in document
        const visible = entries
          .filter(en => en.isIntersecting)
          .sort((a, b) => a.target.offsetTop - b.target.offsetTop);
        if (visible.length > 0) {
          setActive(visible[0].target.id);
        }
      }, {
        rootMargin: '-80px 0px -60% 0px',
        threshold: 0
      });

      items.forEach(it => observer.observe(it.el));
    }
  }

  /* ─── FAQ TRANSFORM ─── */
  // Pattern attendu : <h2>FAQ</h2> puis une série de h3 (questions) suivis de p/ul/etc (réponses)
  function transformFAQ(root) {
    const faq = root.querySelector('[data-article-faq]');
    if (!faq) return;

    const h2 = faq.querySelector('h2');
    if (!h2) return;

    // Détecte les questions : h3, h4, ou <p><strong>Question?</strong></p>
    // Approche : on parcourt les enfants directs après le h2 et on regroupe.
    const children = Array.from(faq.children);
    const h2Index = children.indexOf(h2);
    if (h2Index === -1) return;
    const after = children.slice(h2Index + 1);

    // Groupe : chaque question = un nouveau bucket
    const buckets = []; // { question: HTMLElement, content: HTMLElement[] }
    let current = null;
    after.forEach(node => {
      if (isQuestion(node)) {
        current = { question: node, content: [] };
        buckets.push(current);
      } else if (current) {
        current.content.push(node);
      }
    });

    if (buckets.length === 0) return;

    // Construit le markup details/summary
    const frag = document.createDocumentFragment();
    buckets.forEach(b => {
      const details = document.createElement('details');
      details.setAttribute('data-faq-item', '');
      const summary = document.createElement('summary');
      summary.setAttribute('data-faq-summary', '');
      summary.textContent = b.question.textContent.trim();
      const content = document.createElement('div');
      content.setAttribute('data-faq-content', '');
      b.content.forEach(c => content.appendChild(c.cloneNode(true)));
      details.appendChild(summary);
      details.appendChild(content);
      frag.appendChild(details);
    });

    // Retire les éléments originaux et insère le fragment
    after.forEach(n => n.remove());
    faq.appendChild(frag);
  }

  function isQuestion(node) {
    if (!node || node.nodeType !== 1) return false;
    const tag = node.tagName.toLowerCase();
    if (tag === 'h3' || tag === 'h4') return true;
    // Détecte <p><strong>Question?</strong></p>
    if (tag === 'p') {
      const strong = node.querySelector(':scope > strong:only-child');
      if (strong && strong.textContent.trim().endsWith('?')) return true;
    }
    return false;
  }

  /* ─── STICKY MOBILE ─── */
  function setupStickyMobile(root) {
    const sticky = root.querySelector('[data-sticky-mobile]');
    if (!sticky) return;

    let revealed = false;
    function check() {
      const docH = document.documentElement.scrollHeight - window.innerHeight;
      const pct = docH > 0 ? (window.pageYOffset / docH) : 0;
      if (pct >= 0.4 && !revealed) {
        sticky.removeAttribute('hidden');
        revealed = true;
      } else if (pct < 0.3 && revealed) {
        sticky.setAttribute('hidden', '');
        revealed = false;
      }
    }

    let ticking = false;
    window.addEventListener('scroll', () => {
      if (!ticking) {
        requestAnimationFrame(() => { check(); ticking = false; });
        ticking = true;
      }
    }, { passive: true });
    check();
  }

  /* ─── UTILS ─── */
  function escapeHTML(s) {
    return String(s).replace(/[&<>"']/g, c => ({
      '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'
    })[c]);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
